const DEFAULT_TRANSITION_FALLBACK_MS = 650
const DEFAULT_IDLE_TIMEOUT_MS = 500

const getDefaultTarget = () =>
    typeof window !== 'undefined' ? window : typeof globalThis !== 'undefined' ? globalThis : null

export function createPanelNavSideEffectQueue({
    run = null,
    runBatch = null,
    target = getDefaultTarget(),
    transitionFallbackMs = DEFAULT_TRANSITION_FALLBACK_MS,
    idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
}) {
    const queue = []
    let transitionFallbackHandle = null
    let idleHandle = null
    let cancelIdle = null

    const clearTransitionFallback = () => {
        if (transitionFallbackHandle == null || !target?.clearTimeout) return
        target.clearTimeout(transitionFallbackHandle)
        transitionFallbackHandle = null
    }

    const clearIdle = () => {
        if (idleHandle == null) return
        cancelIdle?.(idleHandle)
        idleHandle = null
        cancelIdle = null
    }

    const runQueuedEffects = () => {
        idleHandle = null
        cancelIdle = null

        const effects = queue.splice(0, queue.length)
        if (typeof runBatch === 'function') {
            try {
                runBatch(effects)
            } catch (error) {
                console.error('Error running deferred panel navigation side effects:', error)
            }
            return
        }

        if (typeof run !== 'function') return

        for (const effect of effects) {
            try {
                run(effect)
            } catch (error) {
                console.error('Error running deferred panel navigation side effect:', error)
            }
        }
    }

    const flushAfterTransition = () => {
        clearTransitionFallback()
        if (!queue.length || idleHandle != null) return

        if (typeof target?.requestIdleCallback === 'function') {
            idleHandle = target.requestIdleCallback(runQueuedEffects, { timeout: idleTimeoutMs })
            cancelIdle =
                typeof target.cancelIdleCallback === 'function'
                    ? handle => target.cancelIdleCallback(handle)
                    : null
            return
        }

        if (typeof target?.setTimeout === 'function') {
            idleHandle = target.setTimeout(runQueuedEffects, 0)
            cancelIdle = handle => target.clearTimeout?.(handle)
            return
        }

        runQueuedEffects()
    }

    const enqueue = effect => {
        queue.push(effect)
        clearIdle()
        if (transitionFallbackHandle != null || !target?.setTimeout) return

        transitionFallbackHandle = target.setTimeout(() => {
            transitionFallbackHandle = null
            flushAfterTransition()
        }, transitionFallbackMs)
    }

    const cancel = () => {
        clearTransitionFallback()
        clearIdle()
        queue.splice(0, queue.length)
    }

    return {
        cancel,
        enqueue,
        flushAfterTransition,
        getPendingCount: () => queue.length,
    }
}
